import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  CalendarMonth,
  Contacts,
  Delete,
  DirectionsRun,
  Edit,
  ExpandLess,
  ExpandMore,
  FavoriteBorder,
  MenuBook,
  MoreHoriz,
  PeopleOutline,
  Place,
  PersonAdd,
  Restaurant,
  Storefront,
  Warning,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';
import FullScreenImageViewer from '../components/FullScreenImageViewer';
import Layout from '../components/Layout';
import { InviteGuestDialog } from '../components/InviteGuestDialog';
import { HouseDialog } from '../components/HouseDialog';
import { BedroomsSummary } from '../components/BedroomsSummary';
import { AmenitiesSummary } from '../components/AmenitiesSummary';
import { buildGoogleMapsUrl } from '../utils/externalLinks';
import { Bedroom } from '../types/bedroom';
import { AmenityId } from '../types/amenity';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface House {
  id: string;
  name: string;
  shortDescription: string;
  description: string;
  address?: string;
  city?: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
  images?: Array<{
    key?: string;
    url: string;
    originalName: string;
  }>;
  host: User;
  coHosts?: User[];
  guests?: User[];
  bedrooms?: Bedroom[];
  amenities?: AmenityId[];
}

type SectionKey = 'guide' | 'activities' | 'shops' | 'restaurants' | 'calendar' | 'contacts' | 'problems' | 'wishlist' | 'members';

interface CategoryItem {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  sections: Array<{
    key: SectionKey;
    label: string;
    icon: React.ReactNode;
  }>;
}

const HouseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [house, setHouse] = useState<House | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [bedroomsOpen, setBedroomsOpen] = useState(false);
  const [amenitiesOpen, setAmenitiesOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const houseImages = house?.images ?? [];
  const hasHouseImages = houseImages.length > 0;

  const fetchHouse = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/houses/${id}`);
      setHouse(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('houseDetail.failedFetch'));
      console.error('Error fetching house:', err);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get('/users/me');
      setCurrentUser(response.data);
    } catch (err: any) {
      console.error('Error fetching current user:', err);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchHouse();
      fetchCurrentUser();
    }
  }, [id, fetchHouse, fetchCurrentUser]);

  const handleBack = () => {
    navigate('/');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setEditDialogOpen(true);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/houses/${id}`);
      setDeleteDialogOpen(false);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || t('dashboard.failedDelete'));
      setDeleteDialogOpen(false);
    }
  };

  const handleOpenImageViewer = (index = 0) => {
    setSelectedImageIndex(index);
    setImageViewerOpen(true);
  };

  const getSectionPath = (sectionKey: SectionKey) => {
    switch (sectionKey) {
      case 'guide':
        return `/houses/${id}/articles`;
      case 'activities':
        return `/houses/${id}/activities`;
      case 'shops':
        return `/houses/${id}/shops`;
      case 'restaurants':
        return `/houses/${id}/restaurants`;
      case 'calendar':
        return `/houses/${id}/calendar`;
      case 'contacts':
        return `/houses/${id}/contacts`;
      case 'problems':
        return `/houses/${id}/problems`;
      case 'wishlist':
        return `/houses/${id}/wishlist`;
      case 'members':
        return `/houses/${id}/members`;
      default:
        return `/houses/${id}`;
    }
  };

  const handleSectionOpen = (sectionKey: SectionKey) => {
    navigate(getSectionPath(sectionKey));
  };

  const categories: CategoryItem[] = [
    {
      title: t('houseDetail.categories.guideActivities.title'),
      description: t('houseDetail.categories.guideActivities.description'),
      sections: [
        { key: 'guide', label: t('houseDetail.tabs.guide'), icon: <MenuBook /> },
        { key: 'activities', label: t('houseDetail.tabs.activities'), icon: <DirectionsRun /> },
        { key: 'shops', label: t('houseDetail.tabs.shops'), icon: <Storefront /> },
        { key: 'restaurants', label: t('houseDetail.tabs.restaurants'), icon: <Restaurant /> },
      ],
    },
    {
      title: t('houseDetail.categories.calendarReservations.title'),
      description: t('houseDetail.categories.calendarReservations.description'),
      sections: [
        { key: 'calendar', label: t('houseDetail.tabs.calendar'), icon: <CalendarMonth /> },
      ],
    },
    {
      title: t('houseDetail.categories.other.title'),
      sections: [
        { key: 'contacts', label: t('houseDetail.tabs.contacts'), icon: <Contacts /> },
        { key: 'wishlist', label: t('houseDetail.tabs.wishlist'), icon: <FavoriteBorder /> },
        { key: 'problems', label: t('houseDetail.tabs.problems'), icon: <Warning /> },
      ],
    },
  ];

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  if (error || !house) {
    return (
      <Layout>
        <Container maxWidth="lg">
          <Box sx={{ py: 4 }}>
            <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 3 }}>
              {t('houseDetail.backToDashboard')}
            </Button>
            <Alert severity="error">{error || t('houseDetail.notFound')}</Alert>
          </Box>
        </Container>
      </Layout>
    );
  }

  const isOwner = currentUser && house.host ? house.host.id === currentUser.id : false;
  const isHost = currentUser
    ? isOwner || Boolean(house.coHosts?.some((member) => member.id === currentUser.id))
    : false;
  const canManageHouseMembers = isHost;
  const locationLabel = [house.address, house.city].filter(Boolean).join(', ');
  const googleMapsUrl = buildGoogleMapsUrl(house.latitude, house.longitude, locationLabel);

  return (
    <Layout title={t('houseDetail.layoutTitle', { name: house.name })}>
      <Container maxWidth="xl">
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          {t('houseDetail.backToDashboard')}
        </Button>

        <Paper sx={{ mb: 3, overflow: 'hidden', borderRadius: 2 }}>
          <Grid container>
            <Grid item xs={12} md={12}>
              <Box
                role={hasHouseImages ? 'button' : undefined}
                tabIndex={hasHouseImages ? 0 : -1}
                aria-label={hasHouseImages ? t('common.labels.image') : undefined}
                onClick={hasHouseImages ? () => handleOpenImageViewer(0) : undefined}
                onKeyDown={hasHouseImages ? (event: React.KeyboardEvent<HTMLElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOpenImageViewer(0);
                  }
                } : undefined}
                sx={{
                  minHeight: { xs: 260, md: 380 },
                  position: 'relative',
                  background: house.images?.[0]?.url
                    ? `linear-gradient(180deg, rgba(14,18,15,0.08) 12%, rgba(14,18,15,0.76) 100%), url(${house.images[0].url}) center/cover`
                    : 'linear-gradient(135deg, rgba(35,68,54,0.96) 0%, rgba(75,108,91,0.94) 52%, rgba(200,107,74,0.76) 100%)',
                  cursor: hasHouseImages ? 'zoom-in' : 'default',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 2,
                    p: { xs: 1.5, md: 3 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h3" sx={{ color: 'common.white', maxWidth: 620 }}>
                        {house.name}
                      </Typography>
                      {house.shortDescription && (
                        <Typography variant="body1" sx={{ mt: 1.5, maxWidth: 620, color: 'rgba(255,250,245,0.84)' }}>
                          {house.shortDescription}
                        </Typography>
                      )}
                    </Box>
                    {isHost && (
                      <IconButton
                        size="small"
                        onClick={(event) => handleMenuOpen(event)}
                        sx={{
                          bgcolor: 'rgba(255,250,245,0.88)',
                          '&:hover': { bgcolor: 'rgba(255,250,245,1)' },
                          flexShrink: 0,
                          ml: 1,
                        }}
                      >
                        <MoreHoriz />
                      </IconButton>
                    )}
                  </Box>
                  {locationLabel && googleMapsUrl && (
                    <Stack
                      component="a"
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      onClick={(event) => event.stopPropagation()}
                      sx={{
                        alignSelf: 'flex-start',
                        maxWidth: 620,
                        color: 'rgba(255,250,245,0.92)',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          color: 'common.white',
                        },
                      }}
                    >
                      <Place fontSize="small" />
                      <Typography variant="body1" sx={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                        {locationLabel}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={canManageHouseMembers ? 8 : 12}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                  <Chip label={`${t('houseDetail.hostedBy')} ${house.host.firstName} ${house.host.lastName}`} />
                  <Chip label={`${t('houseDetail.sharedWith')} ${(house.coHosts?.length || 0) + (house.guests?.length || 0)}`} />
                </Stack>
                <Typography variant="body1" color="text.secondary">
                  {house.description || t('houseDetail.browseSections')}
                </Typography>

                {house.bedrooms && house.bedrooms.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      onClick={() => setBedroomsOpen((prev) => !prev)}
                      endIcon={bedroomsOpen ? <ExpandLess /> : <ExpandMore />}
                      sx={{ mb: 0.5, textTransform: 'none' }}
                    >
                      {t('bedrooms.title')}
                    </Button>
                    <Collapse in={bedroomsOpen}>
                      <BedroomsSummary bedrooms={house.bedrooms} />
                    </Collapse>
                  </Box>
                )}

                {house.amenities && house.amenities.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      onClick={() => setAmenitiesOpen((prev) => !prev)}
                      endIcon={amenitiesOpen ? <ExpandLess /> : <ExpandMore />}
                      sx={{ mb: 0.5, textTransform: 'none' }}
                    >
                      {t('amenities.title')}
                    </Button>
                    <Collapse in={amenitiesOpen}>
                      <AmenitiesSummary amenities={house.amenities as AmenityId[]} />
                    </Collapse>
                  </Box>
                )}
              </Grid>
              {canManageHouseMembers && <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'rgba(35,68,54,0.04)' }}>

                  <Stack spacing={1.25}>
                    {isHost && (
                      <Button variant="contained" startIcon={<PersonAdd />} onClick={() => setInviteDialogOpen(true)}>
                        {t('houseDetail.inviteGuest')}
                      </Button>
                    )}
                    <Button variant="outlined" startIcon={<PeopleOutline />} onClick={() => handleSectionOpen('members')}>
                      {t('houseDetail.actions.manageMembers')}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>}
            </Grid>
          </Box>
        </Paper>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {categories.map((category) => {
            return (
              <Grid item xs={12} md={4} key={category.title}>
                <Paper
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    userSelect: 'none',
                  }}
                >
                  <Stack spacing={2} sx={{ height: '100%' }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      {category.icon && <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: 'primary.main',
                        }}
                      >
                        {category.icon}
                      </Box>}
                      <Box>
                        <Typography variant="h6">{category.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {category.description}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack spacing={1} sx={{ mt: 'auto' }}>
                      {category.sections.map((section) => (
                        <Button
                          key={section.key}
                          variant="outlined"
                          startIcon={section.icon}
                          onClick={() => handleSectionOpen(section.key)}
                          sx={{ justifyContent: 'flex-start' }}
                        >
                          {section.label}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {isHost && (
          <InviteGuestDialog
            open={inviteDialogOpen}
            onClose={() => setInviteDialogOpen(false)}
            houseId={id!}
            houseName={house.name}
            onSuccess={fetchHouse}
          />
        )}

        <FullScreenImageViewer
          images={houseImages}
          open={imageViewerOpen}
          selectedIndex={selectedImageIndex}
          title={house.name}
          onClose={() => setImageViewerOpen(false)}
          onNavigate={setSelectedImageIndex}
        />

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEdit}>
            <Edit fontSize="small" sx={{ mr: 1 }} />
            {t('dashboard.menuEdit')}
          </MenuItem>
          {isOwner && (
            <MenuItem onClick={handleDeleteClick}>
              <Delete fontSize="small" sx={{ mr: 1 }} />
              {t('dashboard.menuDelete')}
            </MenuItem>
          )}
        </Menu>

        <HouseDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={fetchHouse}
          house={house as any}
        />

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>{t('dashboard.deleteTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('dashboard.deleteMessage')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.actions.cancel')}</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              {t('common.actions.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default HouseDetail;
